<template>
    <div>
        <DataTable
            :columns="columns"
            :items="webhooks"
            :currentPage="1"
            :totalPages="1"
            :perPage="Math.max(webhooks.length, 10)"
            :selectedItems="[]"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            :loading="loading"
        >
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <div class="ppv2-flex-1"></div>
                    <a
                        href="/back-office/v3/applications/documentation"
                        class="ppv2-min-h-[40px] ppv2-text-black ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-black ppv2-border-opacity-20 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-transition-all ppv2-shadow-lg"
                    >
                        <span
                            class="ppv2-flex ppv2-items-center ppv2-gap-3 poppinsMedium ppv2-text-black"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <FileText :size="20" color="currentColor" />
                            {{ $t('api.view_documentation') }}
                        </span>
                    </a>
                    <BaseButton
                        variant="outline"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-px-4"
                        @click="openGuide"
                    >
                        <BookOpen :size="18" />
                        <span class="poppinsRegular">{{ $t('webhooks.guide') }}</span>
                    </BaseButton>
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openForm(null)"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('webhooks.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <template #column-url="{ item }">
                <div class="ppv2-text-left ppv2-max-w-[360px]">
                    <span
                        class="ppv2-block ppv2-truncate ppv2-text-black poppinsRegular"
                        :title="item.url"
                        :style="{ fontSize: normalizeFontSize(14) }"
                        >{{ item.url }}</span
                    >
                    <span v-if="item.description" class="ppv2-block ppv2-truncate ppv2-text-xs ppv2-text-gray-500">{{
                        item.description
                    }}</span>
                </div>
            </template>

            <template #column-events="{ item }">
                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-1.5 ppv2-max-w-[340px] ppv2-whitespace-normal">
                    <code
                        v-for="event in item.events"
                        :key="event"
                        class="ppv2-text-xs ppv2-bg-white ppv2-border ppv2-border-[#E2E6EC] ppv2-rounded-md ppv2-px-1.5"
                        >{{ event }}</code
                    >
                </div>
            </template>

            <template #column-status="{ item }">
                <span class="ppv2-text-xs poppinsMedium ppv2-px-2.5 ppv2-py-0.5 ppv2-rounded-full" :class="status(item).cls">{{
                    status(item).label
                }}</span>
            </template>

            <template #column-last_delivery="{ item }">
                <span class="ppv2-text-[#444C57]" :style="{ fontSize: normalizeFontSize(13) }">{{ lastDelivery(item) }}</span>
            </template>

            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        :ref="`actionButton-${item.id}`"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        @click.stop="toggleMenu(item.id)"
                    >
                        <MoreVertical :size="18" />
                    </button>
                    <div
                        v-if="activeMenu === item.id"
                        :ref="`actionMenu-${item.id}`"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="menuPosition"
                    >
                        <div
                            v-for="action in actions"
                            :key="action.key"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer"
                            @click="run(action.key, item)"
                        >
                            <component :is="action.icon" :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{ action.label }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </DataTable>

        <WebhookForm v-model="showForm" :webhook="editing" :events="events" :token="token" @saved="onSaved" />

        <!-- Secret : affiché une seule fois (création / régénération), le serveur ne le renvoie plus ensuite. -->
        <CustomDialog v-model="showSecret" :defaultTitle="$t('webhooks.secret.title')" width="600px">
            <template #content>
                <div class="ppv2-flex ppv2-flex-col ppv2-gap-3 ppv2-py-2">
                    <p class="ppv2-text-sm ppv2-text-amber-700">{{ $t('webhooks.secret.warning') }}</p>
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-bg-[#F7FAFC] ppv2-p-3 ppv2-rounded-xl ppv2-border ppv2-border-[#E2E8F0]"
                    >
                        <code class="ppv2-text-black ppv2-break-all">{{ secret }}</code>
                        <button class="ppv2-text-[#7D2DBD] hover:ppv2-opacity-70" @click="copy(secret)">
                            <Copy :size="18" />
                        </button>
                    </div>
                    <p class="ppv2-text-xs ppv2-text-gray-500">{{ $t('webhooks.secret.help') }}</p>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="outline" :customHeight="48" customClass="!ppv2-min-w-[160px]" @click="showSecret = false">{{
                    $t('codeModal.close')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <CustomDialog v-model="showDeliveries" :defaultTitle="$t('webhooks.deliveries.title')" width="760px">
            <template #content>
                <p v-if="!deliveries.length" class="ppv2-text-center ppv2-text-gray-500 ppv2-py-6">
                    {{ $t('webhooks.deliveries.empty') }}
                </p>
                <table v-else class="ppv2-w-full ppv2-text-sm">
                    <thead>
                        <tr class="ppv2-bg-[#F3EAFA] ppv2-text-left">
                            <th class="ppv2-px-3 ppv2-py-2">{{ $t('webhooks.deliveries.event') }}</th>
                            <th class="ppv2-px-3 ppv2-py-2">{{ $t('webhooks.deliveries.result') }}</th>
                            <th class="ppv2-px-3 ppv2-py-2">{{ $t('webhooks.deliveries.attempt') }}</th>
                            <th class="ppv2-px-3 ppv2-py-2">{{ $t('webhooks.deliveries.date') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="d in deliveries" :key="d.id" class="ppv2-border-b ppv2-border-[#EEF1F5]">
                            <td class="ppv2-px-3 ppv2-py-2">
                                <code class="ppv2-text-xs">{{ d.event_type }}</code>
                            </td>
                            <td class="ppv2-px-3 ppv2-py-2">
                                <span
                                    class="ppv2-text-xs poppinsMedium ppv2-px-2 ppv2-py-0.5 ppv2-rounded-full"
                                    :class="d.succeeded ? okCls : koCls"
                                    >{{ d.status_code || '—' }}</span
                                >
                                <span v-if="d.error" class="ppv2-block ppv2-text-xs ppv2-text-red-600 ppv2-mt-1">{{
                                    d.error
                                }}</span>
                                <span v-else class="ppv2-text-xs ppv2-text-gray-500"> · {{ d.duration_ms }} ms</span>
                            </td>
                            <td class="ppv2-px-3 ppv2-py-2">{{ d.attempt }}</td>
                            <td class="ppv2-px-3 ppv2-py-2 ppv2-whitespace-nowrap">{{ formatDate(d.created_at) }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    :customHeight="48"
                    customClass="!ppv2-min-w-[160px]"
                    @click="showDeliveries = false"
                    >{{ $t('codeModal.close') }}</BaseButton
                >
            </template>
        </CustomDialog>

        <CustomDialog v-model="showGuide" :defaultTitle="$t('webhooks.guide')" width="860px">
            <template #content>
                <!-- HTML rendu côté serveur depuis WebhookDocumentation::GUIDE (constante, entrées HTML échappées). -->
                <div class="webhookGuide ppv2-text-black" v-html="guideHtml"></div>
            </template>
            <template #actions>
                <BaseButton variant="outline" :customHeight="48" customClass="!ppv2-min-w-[160px]" @click="showGuide = false">{{
                    $t('codeModal.close')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <CustomDialog
            v-model="showDelete"
            :defaultTitle="$t('webhooks.delete_title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('webhooks.delete_confirm') }}
                </p>
            </template>
            <template #actions>
                <BaseButton variant="outline" :customHeight="45" customClass="!ppv2-min-w-[170px]" @click="showDelete = false">{{
                    $t('common.cancel')
                }}</BaseButton>
                <BaseButton variant="danger" :customHeight="45" customClass="!ppv2-min-w-[170px]" @click="confirmDelete">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import 'moment/locale/fr'
import { FilePen, Trash2, Plus, MoreVertical, Copy, FileText, Send, History, KeyRound, BookOpen } from 'lucide-vue'
import DataTable from '../../Shared/DataTable.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import WebhookForm from './WebhookForm.vue'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'WebhooksTable',
    components: { DataTable, BaseButton, CustomDialog, WebhookForm, Plus, MoreVertical, Copy, FileText, BookOpen },
    props: {
        token: { type: String, default: '' },
    },
    data() {
        return {
            webhooks: [],
            events: [],
            loading: false,
            activeMenu: null,
            menuPosition: {},
            showForm: false,
            editing: null,
            showSecret: false,
            secret: '',
            showDeliveries: false,
            deliveries: [],
            showDelete: false,
            showGuide: false,
            guideHtml: '',
            deleting: null,
            okCls: 'ppv2-bg-green-100 ppv2-text-green-800',
            koCls: 'ppv2-bg-red-100 ppv2-text-red-700',
        }
    },
    computed: {
        columns() {
            return [
                { key: 'url', label: this.$t('webhooks.columns.url'), sortable: false },
                { key: 'events', label: this.$t('webhooks.columns.events'), sortable: false },
                { key: 'status', label: this.$t('webhooks.columns.status'), sortable: false },
                { key: 'last_delivery', label: this.$t('webhooks.columns.last_delivery'), sortable: false },
                { key: 'actions', label: this.$t('common.actions'), sortable: false },
            ]
        },
        actions() {
            return [
                { key: 'edit', icon: FilePen, label: this.$t('common.edit') },
                { key: 'test', icon: Send, label: this.$t('webhooks.actions.test') },
                { key: 'deliveries', icon: History, label: this.$t('webhooks.actions.deliveries') },
                { key: 'roll', icon: KeyRound, label: this.$t('webhooks.actions.roll_secret') },
                { key: 'delete', icon: Trash2, label: this.$t('common.delete') },
            ]
        },
        config() {
            return { headers: { Authorization: `Bearer ${this.token}` } }
        },
    },
    mounted() {
        moment.locale(this.$i18n.locale)
        this.fetch()
        axios.get('/api/v3/webhooks/events', this.config).then(({ data }) => (this.events = data.data))
        document.addEventListener('click', this.closeMenu)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeMenu)
    },
    methods: {
        normalizeSize,
        normalizeFontSize,
        async fetch() {
            this.loading = true
            try {
                this.webhooks = (await axios.get('/api/v3/webhooks', this.config)).data.data
                if (!this.webhooks.length && !this.guideHtml) this.openGuide()
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('common.load_error'))
            } finally {
                this.loading = false
            }
        },
        status(item) {
            if (!item.active) return { label: this.$t('webhooks.status.inactive'), cls: 'ppv2-bg-gray-100 ppv2-text-gray-600' }
            if (item.last_delivery && !item.last_delivery.succeeded)
                return { label: this.$t('webhooks.status.failing'), cls: this.koCls }
            return { label: this.$t('webhooks.status.active'), cls: this.okCls }
        },
        lastDelivery(item) {
            const d = item.last_delivery
            return d ? `${d.status_code || this.$t('webhooks.deliveries.no_response')} · ${moment(d.created_at).fromNow()}` : '—'
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm:ss')
        },
        toggleMenu(id) {
            this.activeMenu = this.activeMenu === id ? null : id
            if (!this.activeMenu) return
            const btn = this.$refs[`actionButton-${id}`]
            const rect = (Array.isArray(btn) ? btn[0] : btn).getBoundingClientRect()
            this.menuPosition = { top: `${rect.bottom + 4}px`, left: `${rect.right - 220}px` }
        },
        closeMenu() {
            this.activeMenu = null
        },
        async openGuide() {
            if (!this.guideHtml) {
                try {
                    this.guideHtml = (await axios.get('/api/v3/webhooks/guide', this.config)).data.data.html
                } catch (error) {
                    return this.$toast.error(this.$t('common.load_error'))
                }
            }
            this.showGuide = true
        },
        openForm(item) {
            this.editing = item
            this.showForm = true
        },
        onSaved(webhook) {
            if (webhook.secret) this.reveal(webhook.secret)
            this.fetch()
        },
        reveal(secret) {
            this.secret = secret
            this.showSecret = true
        },
        async run(action, item) {
            this.activeMenu = null
            if (action === 'edit') return this.openForm(item)
            if (action === 'delete') {
                this.deleting = item
                this.showDelete = true
                return
            }
            try {
                if (action === 'test') {
                    const d = (await axios.post(`/api/v3/webhooks/${item.id}/test`, {}, this.config)).data.data
                    d.succeeded
                        ? this.$toast.success(this.$t('webhooks.test_success', { code: d.status_code, ms: d.duration_ms }))
                        : this.$toast.error(this.$t('webhooks.test_failed', { reason: d.error || d.status_code }))
                    this.fetch()
                } else if (action === 'deliveries') {
                    this.deliveries = (await axios.get(`/api/v3/webhooks/${item.id}/deliveries`, this.config)).data.data
                    this.showDeliveries = true
                } else if (action === 'roll') {
                    this.reveal((await axios.post(`/api/v3/webhooks/${item.id}/roll-secret`, {}, this.config)).data.data.secret)
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('common.generic_error'))
            }
        },
        async confirmDelete() {
            try {
                await axios.delete(`/api/v3/webhooks/${this.deleting.id}`, this.config)
                this.$toast.success(this.$t('webhooks.delete_success'))
                this.fetch()
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('common.delete_error'))
            } finally {
                this.showDelete = false
                this.deleting = null
            }
        },
        copy(text) {
            navigator.clipboard
                .writeText(text)
                .then(() => this.$toast.success(this.$t('common.copied_clipboard')))
                .catch(() => this.$toast.error(this.$t('common.copy_error')))
        },
    },
}
</script>

<style scoped lang="scss">
.webhookGuide {
    font-size: 14px;
    line-height: 1.6;

    :deep(h2) {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 12px;
    }
    :deep(h3) {
        font-size: 16px;
        font-weight: 600;
        margin: 24px 0 8px;
        color: #7d2dbd;
    }
    :deep(p),
    :deep(ul),
    :deep(ol) {
        margin: 0 0 12px;
    }
    :deep(ul) {
        list-style: disc;
        padding-left: 20px;
    }
    :deep(ol) {
        list-style: decimal;
        padding-left: 20px;
    }
    :deep(code) {
        font-size: 12px;
        background: #f3f4f6;
        border-radius: 4px;
        padding: 1px 4px;
    }
    :deep(pre) {
        background: #021832;
        color: #e5e7eb;
        border-radius: 10px;
        padding: 14px;
        overflow-x: auto;
        margin: 0 0 12px;
    }
    :deep(pre code) {
        background: none;
        padding: 0;
        color: inherit;
    }
    :deep(table) {
        width: 100%;
        border-collapse: collapse;
        margin: 0 0 12px;
        font-size: 13px;
    }
    :deep(th) {
        background: #f3eafa;
        text-align: left;
        padding: 6px 10px;
    }
    :deep(td) {
        border-bottom: 1px solid #eef1f5;
        padding: 6px 10px;
        vertical-align: top;
    }
}

.actionsDropdown {
    background: white;
    border: 1px solid #e2e8f0;

    .actionItem {
        color: #4a5568;
        &:hover {
            background: #f7fafc;
            color: #7d2dbd;
        }
    }
}
</style>
